/*! projector-utils.js */
const FIREFOX_REGEX = /Firefox\//;
const CHROME_IOS_REGEX = /CriOS/;
const REGEXP_MOBILE = /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
const SAFARI_VERSION_REGEX = /version\/(\d+)(\.(\d+))?(\.(\d+))?/i;
const IE_EDGE_REGEX = /Edge\/|Trident\/|MSIE /;

const PLAYER_TYPES = {
    mini: "mini",
    radio: "radio"
};

let autoplayAllowed = false;

class ProjectorUtils {
    static isAllowedToAutoplay(playerOptions) {
        return !this.isRadioPlayer(playerOptions)
            && (
                !ProjectorUtils.isAutoplayDisabledBrowser()
                || playerOptions.muted
                || ProjectorUtils.isMutedOverlayShowing(playerOptions)
            );
    }

    static isMutedOverlayShowing(playerOptions) {
        return !this.isRadioPlayer(playerOptions)
                && (
                    playerOptions.original_user_options.isPlaylistPreloadedPlayer
                    || playerOptions.original_user_options.autoplay
                )
                && ProjectorUtils.isMutedOverlayEnabled(playerOptions);
    }

    static isMutedOverlayEnabled(playerOptions) {
        const { autoplay } = playerOptions.original_user_options;
        return playerOptions.MutedOverlayComponent && isOverlayTreatmentEnabled(playerOptions) && autoplay;
    }

    static isFoundationOverlayEnabled(playerOptions) {
        const { autoplay } = playerOptions.original_user_options;
        const { useFoundationLayout } = playerOptions;
        return !this.isMobileButNotSafari() && useFoundationLayout && isOverlayTreatmentEnabled(playerOptions) && !autoplay;
    }

    static isActionRequiredToPlayAd(isMuted, playerOptions = {}) {
        return ProjectorUtils.isRadioPlayer(playerOptions)
            || (
                !isMuted
                && ProjectorUtils.isAutoplayDisabledBrowser()
            );
    }

    static isHlsSupported() {
        if (ProjectorUtils.isMobile()) {
            return true;
        }

        return Hls.isSupported();
    }

    static isMiniPlayer({ playerType } = {}) {
        return playerType === PLAYER_TYPES.mini;
    }

    /**
     *
     * @param playerType
     *
     *  @returns {boolean}
     */
    static isRadioPlayer({ playerType } = {}) {
        return playerType === PLAYER_TYPES.radio;
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isMobile() {
        return REGEXP_MOBILE.test(this.userAgent());
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isMobileAndroid() {
        return (/Android/i).test(this.userAgent());
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isMobileButNotSafari() {
        return ProjectorUtils.isMobile() && !ProjectorUtils.isSafari();
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isSafari() {
        const ua = this.userAgent().toLocaleLowerCase();
        return ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1;
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isSafariDesktop() {
        return ProjectorUtils.isSafari() && !ProjectorUtils.isMobile();
    }

    static isSafariDesktop11() {
        if (!this.isSafariDesktop()) {
            return false;
        }

        const version = this.getBrowserVersion(this.userAgent(), SAFARI_VERSION_REGEX);
        return version.major === 11 && version.minor === 0 && version.patch === 0;
    }

    /**
     * @returns {boolean}
     */
    static canAutoplayAfterAction() {
        // Safari 11.0.1 & chrome 64+ allows all the rest videos to autoplay with sound if any of the video on the page was played with sound
        // Safari 11.0 requires user's direct click to trigger video play, and it can't be carried over to the next video
        return !(this.isSafariDesktop11() || this.isMobile());
    }

    static isAutoplayDisabledBrowser() {
        return !ProjectorUtils.getAutoplayAllowed();
    }

    static needDirectUserActionToPlay() {
        return !this.getAutoplayAllowed() && (this.isSafariDesktop11() || this.isMobile());
    }

    static setAutoplayAllowed(value) {
        autoplayAllowed = value;
    }

    static getAutoplayAllowed() {
        return autoplayAllowed;
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isFirefox() {
        return FIREFOX_REGEX.test(this.userAgent());
    }

    /**
     * @static
     * @returns {boolean}
     */
    static isChromeIos() {
        return CHROME_IOS_REGEX.test(navigator.userAgent);
    }

    static isIeOrEdge() {
        return IE_EDGE_REGEX.test(this.userAgent());
    }

    /**
     * @static
     * @returns {string}
     */
    static userAgent() {
        return navigator.userAgent;
    }

    static getBrowserVersion(userAgent, regex) {
        const result = regex.exec(userAgent);
        if (!result) {
            return { major: 0, minor: 0, patch: 0 };
        }

        return {
            major: parseInt(result[1], 10) || 0,
            minor: parseInt(result[3], 10) || 0,
            patch: parseInt(result[5], 10) || 0
        };
    }

    static isAdOrContentPlaying(player) {
        if (!player) {
            return false;
        }

        const adController = player.AdController;

        if (adController && adController.adUnit) {
            return adController.isPlaying();
        }

        return !player.paused();
    }
}

function isOverlayTreatmentEnabled(playerOptions) {
    if (!ProjectorUtils.isAutoplayDisabledBrowser() && !playerOptions.start_muted) {
        return false;
    }

    const { player_type: playerType } = playerOptions.metadata;

    return playerType !== "mini" && playerType !== "radio";
}

export default ProjectorUtils;
